import type { GeoArResult } from "../lab-v14/types/GeoArResult";
import {
  identifyPointedFeature,
  type PointIdentifyOptions,
  type PointIdentifyOutcome,
} from "./pointIdentify";
import type { GeoArRangeMeters } from "./geoArRangeScale";

export {
  GEO_AR_RANGE_PRESETS_METERS,
  formatGeoArRange,
  isGeoArRangeAtMaximum,
  isGeoArRangeAtMinimum,
  normalizeGeoArRange,
  stepGeoArRange,
  type GeoArManualRangeMeters,
  type GeoArRangeMeters,
} from "./geoArRangeScale";

export function filterGeoArResultsByRange(results: readonly GeoArResult[], rangeMeters: GeoArRangeMeters) {
  if (rangeMeters === null) return [...results];
  return results.filter((result) => result.distance !== null && result.distance <= rangeMeters);
}

function farMountainScore(result: GeoArResult, options: PointIdentifyOptions, rangeMeters: number) {
  const horizontalAimDegrees = Math.max(2, options.horizontalAimDegrees ?? 8);
  const horizontal = Math.abs(result.relative_angle ?? Number.POSITIVE_INFINITY);
  if (horizontal > horizontalAimDegrees) return null;
  if (result.distance === null || result.distance <= 50_000 || result.distance > rangeMeters) return null;
  if (!["MOUNTAIN", "VOLCANO", "SUMMIT", "HILL"].includes(String(result.feature_type))) return null;

  const horizontalScore = Math.max(0, 1 - horizontal / horizontalAimDegrees);
  const distanceSpan = Math.max(1, rangeMeters - 50_000);
  const distanceScore = Math.max(0, 1 - (result.distance - 50_000) / distanceSpan);
  const score = horizontalScore * 0.64 + distanceScore * 0.18 + result.confidence * 0.18;
  return { result, score };
}

export function identifyPointedFeatureForRange(
  results: readonly GeoArResult[],
  options: PointIdentifyOptions,
  rangeMeters: GeoArRangeMeters,
): PointIdentifyOutcome {
  const filtered = filterGeoArResultsByRange(results, rangeMeters);
  const base = identifyPointedFeature(filtered, options);
  if (rangeMeters === null || base.status !== "NO_MATCH" || rangeMeters <= 50_000 || options.poseStable === false) return base;

  const farCandidates = filtered
    .map((result) => farMountainScore(result, options, rangeMeters))
    .filter((candidate): candidate is NonNullable<typeof candidate> => candidate !== null)
    .sort((left, right) => right.score - left.score || (left.result.distance ?? Infinity) - (right.result.distance ?? Infinity));
  const best = farCandidates[0];
  if (!best || best.score < Math.max(0.34, options.minimumConfidence ?? 0.24)) return base;

  const second = farCandidates[1];
  const ambiguous = Boolean(second && Math.abs(best.score - second.score) < 0.06);
  const confidenceLabel: PointIdentifyOutcome["confidenceLabel"] = best.score >= 0.72 && !ambiguous
    ? "alta"
    : best.score >= 0.48
      ? "media"
      : "baja";

  return {
    status: "MATCH",
    result: best.result,
    score: Math.round(best.score * 1000) / 1000,
    confidenceLabel,
    message: ambiguous
      ? `${best.result.name ?? "Montana"} es la mejor coincidencia de largo alcance, con otra montana cercana en la misma direccion.`
      : `${best.result.name ?? "Montana"} alineada con la reticula dentro del radio manual.`,
    sourceLabel: best.result.data_source || "THERA Range Catalog",
    alternatives: farCandidates.slice(1, 4).map((candidate) => candidate.result.name).filter((name): name is string => Boolean(name)),
  };
}
